// components/Dashboard.js
// npm install @tinymce/tinymce-react
// npm install --save @tinymce/tinymce-react

import React, { useState, useEffect } from "react";
import {
    Button,
    Typography,
    TextField,
    Box,
    Autocomplete,
    Grid2 as Grid,
    FormControlLabel,
    Switch,
    IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    addProperties,
    editProperties,
    getByIDProperties,
} from "./Properties.service";
import SortableList from "react-sortablejs";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { ToastContainer } from "react-toastify";
import MapField from "../../common/MapField";
import TextEditor from "../../common/TextEditor";
import axios from "axios";
import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableItem } from "../../common/SortableItems";
import countriesData from "../../common/api/countryStates";
import { BedOptions, propertyTypeOptions, StatusOptions } from "../../common/api/utils";
import { showErrorToast } from "../../common/util/util";

const validationSchema = yup.object({
    name: yup.string().required("Name is required"),
    rentOrPrice: yup
        .number()
        .min(8, "Rent OR Price should be of minimum 8 characters length")
        .required("Rent OR Price is required"),
    location: yup.string().required("Number is required"),
    mapLong: yup
        .object()
        .shape({
            lat: yup.number().required("Latitude is required"),
            lng: yup.number().required("Longitude is required"),
        })
        .required("Map location is required"),
    bathroom: yup.number().required("Bathrooms is required"),
    bedrooms: yup.string().required("Bedrooms is required"),
    description: yup.string().required("Description is required"),
    features: yup.string().required("Features is required"),
    landSize: yup.number().required("Area is required"),
    images: yup
        .array()
        .min(1, "At least one image is required")
        .required("Images are required"),
    // floorPlan: yup.string(),
    featured: yup.boolean(),
});
const fetchSuggestions = async (query: any) => {
    if (!query) return [];

    try {
        const response = await axios.get(
            `https://nominatim.openstreetmap.org/search`, {
            params: {
                q: query,
                format: "json",
                addressdetails: 1,
                limit: 5,
            }
        }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching suggestions:", error);
        return [];
    }
};
const AddProperty = () => {
    const initialValues = {
        name: "",
        rentOrPrice: 0,
        description: "",
        bathroom: 0,
        location: "",
        bedrooms: "",
        features: "",
        propertyStatus: "",
        status: "",
        mapLong: {
            lat: 51.505,
            lng: -0.09,
        },
        type: "",
        landSize: 0,
        state:'',
        country:'',
        images: [],
        statusValue: "",
        floorPlan: "",
        eceDocument: "",
        featured: false,
    };
    const [states, setStates] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [activeId, setActiveId] = useState<any>(null);
    const id = useParams();
    const navigate = useNavigate();
    const [options, setOptions] = useState<any[]>(BedOptions);

    const [statusOptions] = useState<any[]>(StatusOptions);
    const [typeOptions, setTypeOptions] = useState<any[]>(propertyTypeOptions);
    const [statusValueOptions] = useState<any[]>([
        { id: 1, label: "Active" },
        { id: 2, label: "In Active" },
    ]);
    const handleSearchInput = async (e: any) => {
        const value = e.target.value;
        formik.setFieldValue('location', value);
        const results = await fetchSuggestions(value);
        setSuggestions(results);
    };

    const handleSuggestionClick = (location: any) => {
        formik.setFieldValue('location', location?.display_name)
        formik.setFieldValue('mapLong', { lat: location?.lat, lng: location?.lon })
        setSuggestions([]);
    }
    const formik: any = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (values: any) => {
            const formData = new FormData();
            const scalarFields = [
                "name", "rentOrPrice", "description", "location", "landSize", "bathroom",
                "bedrooms", "features", "propertyStatus", "type", "status",
                "floorPlan", "featured", "state", "country", 'eceDocument'
            ];

            scalarFields.forEach((field) => {
                formData.append(field, values[field]);
            });
            formData.append("mapLong", JSON.stringify(values.mapLong));
            const stripBaseUrl = (url: string) => {
                const base = "http://localhost:3001/";
                return url.startsWith(base) ? url.slice(base.length).replace(/\\/g, "/") : url;
            };

            values.images?.forEach((image: any) => {
                if (typeof image === "string") {
                    const cleanPath = stripBaseUrl(image);
                    formData.append("existingImages", cleanPath);
                } else if (image?.file instanceof File) {
                    formData.append("images", image.file);
                } else {
                    console.warn("Unrecognized image format", image);
                }
            });
            console.log("Submitted Images:", values.images);
            console.log("FormData ready for submission");

            const submitFn = id.id ? editProperties : addProperties;
            submitFn(formData, id.id)
                .then(() => navigate("/properties"))
                .catch((err: any) => {
                    console.error(err.message)
                    console.log("Error submitting form:", err.response?.data);
                    if(err.response?.data?.message.includes("Unexpected field")){
                    showErrorToast("You can only upload up to 10 images at a time"); 
                    }  
                });
        },
    });

    
    useEffect(() => {
        console.log("Errors:", formik.errors);
        console.log("Touched:", formik.touched);
    }, [formik.errors, formik.touched]);

    const handleLocationChange = (coordinates: any) => {
        formik.setFieldValue("mapLong", coordinates);
    };
    const onDrop = (acceptedFiles: any) => {
        const newImages = acceptedFiles.map((file: File) => ({
            preview: URL.createObjectURL(file),
            file,
        }));
        formik.setFieldValue("images", [...(formik.values.images || []), ...newImages]);
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [".png", ".jpg", ".jpeg"] },
    });

    const removeImage = (index: any) => {
        console.log(index, 'index')
        const updatedImages = formik.values.images?.filter((_: any, i: any) => i !== index);
        console.log(updatedImages, 'updatedImages')
        formik.setFieldValue("images", updatedImages);
    };
    const handleCountryChange = (event:any, value:any) => {
        const selectedCountry:any = countriesData.find(country => country.name === value);
        setStates(selectedCountry ? selectedCountry.states : []);
        formik.setFieldValue("country", value);
        formik.setFieldValue("state", "");
        console.log(selectedCountry,'selectedCountry');
        if (selectedCountry.name === "India") {
            setOptions([
                { id: 1, label: "1 BHK" },
                { id: 2, label: "2 BHK" },
                { id: 3, label: "3 BHK" },
                { id: 4, label: "4 BHK" },
            ]);
            setTypeOptions([
                { id: 1, label: "Apartment" },
                { id: 2, label: "Villa" },
                { id: 3, label: "Plot" },
                { id: 4, label: "Independent House" },
            ]);
        } else {
            setOptions(BedOptions);
            setTypeOptions(propertyTypeOptions);
        }
    };
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = formik.values.images?.findIndex((image: any) => image.preview === active.id);
            const newIndex = formik.values.images?.findIndex((image: any) => image.preview === over.id);
            const reorderedImages = arrayMove(formik.values.images, oldIndex, newIndex);
            formik.setFieldValue("images", reorderedImages);
        }
        setActiveId(null);
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor)
    );
    useEffect(() => {
        if (id.id) {
            getByIDProperties(id.id)
                .then((res: any) => {
                    formik.setValues(res);
                    const selectedCountry:any = countriesData.find(country => country.name === res.country);
                    console.log(selectedCountry.states.find((e:any)=>e.name == res.state).name, 'seles');
                    const selectedState = selectedCountry.states.find(
                        (state: any) => state.name === res.state
                    );
                    setStates(selectedCountry ? selectedCountry.states : []);
                    formik.setFieldValue('state', selectedState.name);
                    if (selectedCountry.name === "India") {
                        setOptions([
                            { id: 1, label: "1 BHK" },
                            { id: 2, label: "2 BHK" },
                            { id: 3, label: "3 BHK" },
                            { id: 4, label: "4 BHK" },
                        ]);
                        setTypeOptions([
                            { id: 1, label: "Apartment" },
                            { id: 2, label: "Villa" },
                            { id: 3, label: "Plot" },
                            { id: 4, label: "Independent House" },
                        ]);
                    } else {
                        setOptions(BedOptions);
                        setTypeOptions(propertyTypeOptions);
                    }
                })
                .catch((err) => console.error("Error fetching property:", err.message));
        }
    }, [id.id]);
    return (
        <Box paddingY={4}>
            <h3>{id?.id ? 'Edit' : 'Add'} Properties</h3>
            <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="name" className="form-label">
                            Name
                        </label>
                        <TextField
                            fullWidth
                            name="name"
                            placeholder="Property Name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                        // sx={{ marginBottom: 2 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="rentOrPrice" className="form-label">
                            Rent or Price
                        </label>
                        <TextField
                            fullWidth
                            name="rentOrPrice"
                            placeholder="Rent or Price"
                            type="number"
                            value={formik.values.rentOrPrice}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.rentOrPrice && Boolean(formik.errors.rentOrPrice)
                            }
                            helperText={
                                formik.touched.rentOrPrice && formik.errors.rentOrPrice
                            }
                        // sx={{ marginBottom: 2 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="landSize" className="form-label">
                            Area (sq ft)
                        </label>
                        <TextField
                            fullWidth
                            name="landSize"
                            placeholder="Area (sq ft)"
                            type="number"
                            value={formik.values.landSize}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.landSize && Boolean(formik.errors.landSize)}
                            helperText={formik.touched.landSize && formik.errors.landSize}
                        // sx={{ marginBottom: 2 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="bathroom" className="form-label">
                            Bathrooms
                        </label>
                        <TextField
                            fullWidth
                            name="bathroom"
                            placeholder="Number of Bathrooms"
                            type="number"
                            value={formik.values.bathroom}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.bathroom && Boolean(formik.errors.bathroom)}
                            helperText={formik.touched.bathroom && formik.errors.bathroom}
                        // sx={{ marginBottom: 2 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="location" className="form-label">
                            Location
                        </label>
                        <TextField
                            fullWidth
                            name="location"
                            placeholder="Location"
                            value={formik.values.location}
                            // onChange={formik.handleChange}
                            onChange={handleSearchInput}
                            onBlur={formik.handleBlur}
                            error={formik.touched.location && Boolean(formik.errors.location)}
                            helperText={formik.touched.location && formik.errors.location}
                        // sx={{ marginBottom: 2 }}
                        />
                        {suggestions.length > 0 && (
                            <ul>
                                {suggestions.map((suggestion: any, index: any) => (
                                    <li
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                        {suggestion?.display_name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="country" className="form-label">
                            Country
                        </label>
                        <Autocomplete
                            fullWidth
                            options={countriesData}
                            getOptionLabel={(option:any) => option.name}
                            value={countriesData.find((country:any) => country.name === formik.values.country) || null}
                            onChange={(event, value) => handleCountryChange(event, value ? value.name : "")}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Country"
                                    variant="outlined"
                                    error={formik.touched.country && Boolean(formik.errors.country)}
                                    helperText={formik.touched.country && formik.errors.country}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="state" className="form-label">
                            State
                        </label>
                        <Autocomplete
                                fullWidth
                                options={states}
                                getOptionLabel={(option:any) => option.name}
                                value={states.find((state:any) => state.name === formik.values.state) || null}
                                onChange={(event, value) => formik.setFieldValue("state", value ? value.name : "")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="State"
                                        variant="outlined"
                                        error={formik.touched.state && Boolean(formik.errors.state)}
                                        helperText={formik.touched.state && formik.errors.state}
                                    />
                                )}
                            />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="bedrooms" className="form-label">
                            Select Bedrooms
                        </label>
                        <Autocomplete
                            freeSolo
                            options={options}
                            clearOnEscape
                            // inputValue={inputValue}
                            value={formik.values.bedrooms}
                            onInputChange={(event, newInputValue) => {
                                formik.setFieldValue("bedrooms", newInputValue);
                            }}
                            isOptionEqualToValue={(option, value) =>
                                value && option.id === value.id
                            }
                            // sx={{ marginBottom: 2 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="bedrooms"
                                    placeholder="Select Bedrooms"
                                    variant="outlined"
                                    error={
                                        formik.touched.bedrooms && Boolean(formik.errors.bedrooms)
                                    }
                                    helperText={formik.touched.bedrooms && formik.errors.bedrooms}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="bedrooms" className="form-label">
                            Property Status
                        </label>
                        <Autocomplete
                            freeSolo
                            options={statusOptions}
                            clearOnEscape
                            // disabled={id.id ? false : true}
                            // inputValue={inputValue}
                            value={formik.values.propertyStatus}
                            onInputChange={(event, newInputValue) => {
                                formik.setFieldValue("propertyStatus", newInputValue);
                            }}
                            isOptionEqualToValue={(option: any, value) =>
                                value && option.id === value.id
                            }
                            // sx={{ marginBottom: 2 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="propertyStatus"
                                    placeholder="Select Property Status"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="bedrooms" className="form-label">
                            Property Type
                        </label>
                        <Autocomplete
                            freeSolo
                            options={typeOptions}
                            clearOnEscape
                            // disabled={id.id ? false : true}
                            // inputValue={inputValue}
                            value={formik.values.type}
                            onInputChange={(event, newInputValue) => {
                                formik.setFieldValue("type", newInputValue);
                            }}
                            isOptionEqualToValue={(option, value) =>
                                value && option.id === value.id
                            }
                            // sx={{ marginBottom: 2 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="type"
                                    placeholder="Select Type"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="floorPlan" className="form-label">
                            Floor Plan
                        </label>
                        <TextField
                            type="file"
                            name="floorPlan"
                            id="floorPlan"
                            onChange={(event) => {
                                const target = event.target as HTMLInputElement;
                                const file = target.files?.[0];
                                formik.setFieldValue("floorPlan", file);
                            }}
                            variant="outlined"
                            fullWidth
                        />
                        {formik.values.floorPlan ? (
                            <div style={{ marginTop: "10px" }}>
                                <img src={id.id ? formik.values.floorPlan : URL.createObjectURL(formik.values.floorPlan)} alt="floorplan" />
                            </div>
                        ) : null}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="eceDocument" className="form-label">
                            ECE Documents
                        </label>
                        <TextField
                            type="file"
                            name="eceDocument"
                            id="eceDocument"
                            onChange={(event) => {
                                const target = event.target as HTMLInputElement;
                                const file = target.files?.[0];
                                formik.setFieldValue("eceDocument", file);
                            }}
                            inputProps={{ accept: ".pdf,.doc,.docx" }}
                            variant="outlined"
                            fullWidth
                        />
                        {formik.values.eceDocument ? (
                            <div style={{ marginTop: "10px" }}>
                                {typeof formik.values.eceDocument === "string" ? (
                                    <a
                                        href={formik.values.eceDocument}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#1976d2", textDecoration: "underline" }}
                                    >
                                        View Uploaded Document
                                    </a>
                                ) : (
                                    <a
                                        href={URL.createObjectURL(formik.values.eceDocument)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#1976d2", textDecoration: "underline" }}
                                    >
                                        {formik.values.eceDocument.name}
                                    </a>
                                )}
                            </div>
                        ) : null}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: "25px" }}>
                        <label htmlFor="featured" className="form-label">
                            Featured
                        </label>
                        <FormControlLabel
                            sx={{ marginLeft: "4px" }}
                            control={
                                <Switch
                                    name="featured"
                                    checked={formik.values.featured} // Ensure this is a boolean
                                    onChange={(event) => {
                                        formik.setFieldValue("featured", event.target.checked); // Update the form state
                                    }}
                                    onBlur={formik.handleBlur}
                                    color="primary"
                                />
                            }
                            label={formik.values.featured ? "Yes" : "No"}
                        />
                        {formik.touched.featured && formik.errors.featured && (
                            <div style={{ color: "red", fontSize: "0.8rem" }}>
                                {formik.errors.featured}
                            </div>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <label htmlFor="stautsValue" className="form-label">
                            Status
                        </label>
                        <Autocomplete
                            freeSolo
                            options={statusValueOptions}
                            clearOnEscape
                            // disabled={id.id ? false : true}
                            // inputValue={inputValue}
                            value={formik.values.status}
                            onInputChange={(event, newInputValue) => {
                                formik.setFieldValue("status", newInputValue);
                            }}
                            isOptionEqualToValue={(option, value) =>
                                value && option.id === value.id
                            }
                            // sx={{ marginBottom: 2 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="status"
                                    placeholder="Select Status"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <label htmlFor="images" className="form-label">
                            Add Photos
                        </label>
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: "2px dashed #ccc",
                                padding: 2,
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: isDragActive ? "#f0f0f0" : "inherit",
                            }}
                        >
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <Typography>Drop the images here...</Typography>
                            ) : (
                                <Typography>
                                    Drag & drop some images here, or click to select files
                                </Typography>
                            )}
                        </Box>
                        {formik.errors.images && (
                            <Typography color="error">{formik.errors.images}</Typography>
                        )}
                    </Grid>

                    <Grid size={{ xs: 12, md: 12 }}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            onDragStart={({ active }) => setActiveId(active.id)}
                        >
                            <SortableContext
                                items={formik.values.images.map((image: any) => image.preview)}
                                strategy={verticalListSortingStrategy}
                            ><Grid container spacing={2}>
                                    {formik.values.images.map((image: any, index: any) => (
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <SortableItem
                                                key={image.preview}
                                                id={image.preview}
                                                paramId={id.id}
                                                index={index}
                                                image={image}
                                                onRemove={() => removeImage(index)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </SortableContext>
                            <DragOverlay>
                                {activeId ? (
                                    <img
                                        src={activeId}
                                        alt="drag-preview"
                                        style={{ width: "100px", height: "100px" }}
                                    />
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <MapField
                            label="Pick a Location"
                            value={formik.values.mapLong}
                            onChange={handleLocationChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <label htmlFor="features" className="form-label">
                            Features
                        </label>
                        <TextEditor
                            value={formik.values.features}
                            setFieldWithEditor={(newValue: any) =>
                                formik.setFieldValue("features", newValue)
                            }
                        />
                        {formik.touched.features && Boolean(formik.errors.features) && (
                            <Typography color="error">{formik.errors.features}</Typography>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <label htmlFor="description" className="form-label">
                            Description
                        </label>
                        <TextEditor
                            value={formik.values.description}
                            setFieldWithEditor={(newValue: any) =>
                                formik.setFieldValue("description", newValue)
                            }
                        />
                        {formik.touched.description &&
                            Boolean(formik.errors.description) && (
                                <Typography color="error">
                                    {formik.errors.description}
                                </Typography>
                            )}
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 12 }}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                        <Button component={Link} to="/properties" color="secondary">
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" variant="contained">
                            {id?.id ? "Save" : "Add"} Property
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <ToastContainer position="top-right" autoClose={3000} />
        </Box>
    );
};

export default AddProperty;
